<template>
  <div class="detail-modal">
    <!-- Header -->
    <div class="detail-header">
      <div class="detail-header__left">
        <a
          v-if="!isGroup"
          :href="`/print-invoice/${invoices[0].id}`"
          target="_blank"
          class="detail-print"
          title="Imprimir detalles"
        >
          <v-icon>print</v-icon>
        </a>
        <div>
          <p class="detail-header__eyebrow">
            {{ isGroup ? 'Consolidado' : 'Factura' }}
          </p>
          <h3 class="detail-header__title">
            {{ isGroup ? code : invoices[0].invoiceNumber }}
          </h3>
          <p v-if="isGroup" class="detail-header__sub">
            {{ invoices.length }} facturas
          </p>
        </div>
      </div>
      <v-btn icon class="detail-close" @click="$emit('close-detail')">
        <v-icon>close</v-icon>
      </v-btn>
    </div>

    <!-- Body: una sección por factura. Un consolidado se ve completo desde
         un solo botón, con sus facturas una debajo de otra. -->
    <div class="detail-body">
      <section v-for="inv in invoices" :key="inv.id" class="detail-invoice">
        <div v-if="isGroup" class="detail-invoice__head">
          <h4 class="detail-invoice__title">
            Factura {{ inv.invoiceNumber || '—' }}
          </h4>
          <a
            :href="`/print-invoice/${inv.id}`"
            target="_blank"
            class="detail-print detail-print--inline"
            title="Imprimir detalles de esta factura"
          >
            <v-icon>print</v-icon>
          </a>
        </div>

        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-item__label">Laboratorio</span>
            <span class="detail-item__value">{{ labName(inv) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-item__label">Distribuidor</span>
            <span class="detail-item__value">{{ distName(inv) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-item__label">Factura</span>
            <span class="detail-item__value">{{
              inv.invoiceNumber || '—'
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-item__label"># CIF</span>
            <span class="detail-item__value">{{ formattedCif(inv) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-item__label">Bulto</span>
            <span class="detail-item__value">{{
              inv.packageNumber || '—'
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-item__label">Control</span>
            <span class="detail-item__value">{{
              inv.controlNumber || '—'
            }}</span>
          </div>
          <div v-if="inv.observations" class="detail-item detail-item--full">
            <span class="detail-item__label">Observaciones</span>
            <span class="detail-item__value detail-item__value--wrap">{{
              inv.observations
            }}</span>
          </div>
          <template v-if="showReentryHistory">
            <div
              v-for="field in reentryFields(inv)"
              :key="field.label"
              class="detail-item"
            >
              <span class="detail-item__label">{{ field.label }}</span>
              <span class="detail-item__value">{{ field.value }}</span>
            </div>
          </template>
        </div>

        <!-- Productos -->
        <div class="detail-products">
          <h4 class="detail-products__title">Productos</h4>
          <div class="detail-table-wrap">
            <table class="detail-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Fórmula</th>
                  <th>Reg. Sanitario</th>
                  <th># de Lote</th>
                  <th>Expiración</th>
                  <th class="th-center">Certificados</th>
                  <th class="th-center">Estatus</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(obj, i) in inv.products" :key="i">
                  <td class="detail-cell--strong">
                    {{ obj.product ? obj.product.name : '—' }}
                  </td>
                  <td class="detail-cell--formula">
                    {{ obj.fxFormula || '—' }}
                  </td>
                  <td>{{ obj.sanitaryReg || '—' }}</td>
                  <td>{{ obj.batchNumber || '—' }}</td>
                  <td>{{ dateFormat(obj.expirationDate) }}</td>
                  <td class="th-center">
                    <span
                      class="cert-badge"
                      :class="
                        obj.certificate ? 'cert-badge--yes' : 'cert-badge--no'
                      "
                    >
                      {{ obj.certificate ? 'SÍ' : 'NO' }}
                    </span>
                  </td>
                  <td class="th-center">
                    <span
                      class="status-pill"
                      :class="statusClass(obj.batchStatus)"
                    >
                      {{ getLabelByStatus(obj.batchStatus) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!inv.products || !inv.products.length">
                  <td colspan="7" class="detail-empty">
                    No hay productos asociados a esta factura.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  getColorByStatus,
  getLabelByStatus,
  dateFormat,
} from '@/helpers/TransitHelper'
export default {
  props: {
    // Una factura...
    item: {
      type: Object,
      default: null,
    },
    // ...o las facturas de un consolidado, con su consecutivo (CONS-).
    items: {
      type: Array,
      default: () => [],
    },
    code: {
      type: String,
      default: '',
    },
    showReentryHistory: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    invoices() {
      return this.items.length ? this.items : [this.item]
    },
    isGroup() {
      return this.items.length > 0
    },
  },
  methods: {
    reentryFields(inv) {
      return [
        {
          label: 'Órgano anuente',
          value:
            (inv.organosAnuentes || []).map(organo => organo.name).join(', ') ||
            '—',
        },
        {
          label: 'TAT de rechazo',
          value:
            inv.total_fda == null || inv.total_fda < 0
              ? '—'
              : `${inv.total_fda} día(s) hábil(es)`,
        },
        {
          label: 'Reingreso anterior',
          value: inv.reinitDate
            ? dateFormat(inv.reinitDate, 'DD/MM/YYYY')
            : '—',
        },
        { label: 'Control anterior', value: inv.previousFdControl || '—' },
      ]
    },
    labName(inv) {
      return (inv.lab_invoices && inv.lab_invoices.name) || '—'
    },
    distName(inv) {
      return (inv.dist_invoices && inv.dist_invoices.name) || '—'
    },
    formattedCif(inv) {
      const value = Number(inv.cif)
      if (isNaN(value)) return '—'
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    dateFormat,
    getColorByStatus,
    getLabelByStatus,
    statusClass(status) {
      const map = {
        CREATED: 'status-pill--info',
        IN_PROCESS: 'status-pill--info',
        PROCESSED: 'status-pill--success',
        WARNING: 'status-pill--warning',
        EXPIRED: 'status-pill--danger',
      }
      return map[status] || 'status-pill--default'
    },
  },
}
</script>

<style scoped>
.detail-modal {
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
}

/* ===== Header ===== */
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  background: linear-gradient(135deg, #254f87 0%, #1a3a63 100%);
  color: #ffffff;
}

.detail-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.detail-print {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  transition: background 0.18s ease;
}

.detail-print:hover {
  background: rgba(255, 255, 255, 0.28);
}

.detail-print .v-icon {
  color: #ffffff !important;
  font-size: 20px !important;
}

.detail-header__eyebrow {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);
}

.detail-header__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.detail-header__sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}

.detail-close {
  color: #ffffff !important;
}

.detail-close .v-icon {
  color: #ffffff !important;
}

/* ===== Body ===== */
.detail-body {
  padding: 22px;
}

/* Consolidado: cada factura en su sección, separadas por una línea. */
.detail-invoice + .detail-invoice {
  margin-top: 26px;
  padding-top: 22px;
  border-top: 2px solid #e2e8f0;
}

.detail-invoice__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.detail-invoice__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.detail-print--inline {
  width: 34px;
  height: 34px;
  background: rgba(37, 79, 135, 0.1);
}

.detail-print--inline:hover {
  background: rgba(37, 79, 135, 0.18);
}

.detail-print--inline .v-icon {
  color: #254f87 !important;
  font-size: 18px !important;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 24px;
  margin-bottom: 22px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}

.detail-item--full {
  grid-column: 1 / -1;
}

.detail-item__label {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}

.detail-item__value {
  font-size: 15px;
  font-weight: 600;
  color: #172133;
}

.detail-item__value--wrap {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
}

/* ===== Productos ===== */
.detail-products__title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #172133;
  position: relative;
  padding-bottom: 10px;
}

.detail-products__title::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 32px;
  height: 2px;
  background: #e5252a;
  border-radius: 1px;
}

.detail-table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table thead tr {
  background: #f8fafc;
}

.detail-table th,
.detail-table td {
  text-align: left;
  padding: 10px 14px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13.5px;
  color: #334155;
  vertical-align: top;
}

.detail-table th {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #64748b;
}

.detail-table tbody tr:last-child td {
  border-bottom: 0;
}

.detail-table tbody tr:hover td {
  background: #fafbfd;
}

.th-center {
  text-align: center;
}

.detail-cell--strong {
  font-weight: 700;
  color: #0f172a;
  min-width: 160px;
}

.detail-cell--formula {
  color: #475569;
  font-size: 12.5px;
  line-height: 1.45;
  min-width: 220px;
  max-width: 320px;
}

.cert-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
}

.cert-badge--yes {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.cert-badge--no {
  background: rgba(148, 163, 184, 0.18);
  color: #64748b;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-pill--info {
  background: rgba(37, 79, 135, 0.12);
  color: #254f87;
}

.status-pill--success {
  background: rgba(22, 163, 74, 0.14);
  color: #15803d;
}

.status-pill--warning {
  background: rgba(217, 119, 6, 0.14);
  color: #b45309;
}

.status-pill--danger {
  background: rgba(229, 37, 42, 0.12);
  color: #c91e23;
}

.status-pill--default {
  background: rgba(148, 163, 184, 0.18);
  color: #64748b;
}

.detail-empty {
  text-align: center !important;
  color: #64748b;
  padding: 22px !important;
}

@media (max-width: 720px) {
  .detail-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .detail-body {
    padding: 16px;
  }
}
</style>
